import {
  lazy,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { AppContext, SIB_CONSENT_INIT_FAILURE_MESSAGE } from '../../App';
import { initiateConsent } from '../../Services/PFMClient/ConsentService';
import { ConsentType, FipId, FlowType } from '../../Utils/constants';
import { EVENTCODES, pushEventToParent } from '../../Utils/eventUtils';
import { ROUTES } from '../../Utils/routes';
import { captureException } from '../../Utils/sentryUtils';
import {
  getAltJourney,
  getIsLoanJourney,
  getIsNtb,
  getJfsTheme,
  getMahindraThemeMode,
  getSdkFlowSelection,
  getTenant,
  setReferenceId,
} from '../../Utils/storageUtils';
import {
  altJourneyType,
  FIIntegrationType,
  JfsTheme,
  TenantType,
} from '../../Utils/types';
import { isCommonTenant } from '../CommonTenant/utils/configUtils';
import AAWrapper from './AAWrapper';

// fiType is attached to consent-creation events only for these tenants
const getFiTypeMeta = (flowType?: FlowType | null) => {
  const tenant = getTenant();
  const isSupportedTenant =
    tenant === TenantType.JFS ||
    tenant === TenantType.YESBANK ||
    tenant === TenantType.AU ||
    tenant === TenantType.BOB ||
    tenant === TenantType.BOB_V2;
  if (!isSupportedTenant || flowType === undefined || flowType === null) {
    return undefined;
  }
  return { fiType: FlowType[flowType] };
};

const ConsentFailure = lazy(() => {
  return import('./ConsentFailure');
});

const FIPSelection = lazy(() => {
  return import('./FIPSelection');
});

export const enum AASTEPS {
  FIP_SELECTION,
  CONSENT_CREATION,
  AA_JOURNEY,
  AA_JOURNEY_COMPLETED,
  CONSENT_FAILURE,
}

const AA: React.FC<{
  isError: any;
}> = ({ isError }) => {
  const [isInfoAvailable, setIsInfoAvailable] = useState(true);

  const {
    navigateToDashboard,
    setIsLoading,
    currentFlowType,
    isAdditionalAccountJourney,
    isLoanJourney,
    AAInternalStep,
    setAAInternalStep,
    selectedFIP,
    setSelectedFIP,
    partialLoading,
    setPartialLoading,
    fipSelectionPage,
    currentMetaData,
    setCurrentRoute,
    statusData,
    setShowToastAA,
    setShowAUErrorSheet,
    setShowSIBErrorSheet,
    setSibErrorSheetMessage,
    setBackButtonHandler,
    fdSelectedFips,
    previousRoute,
  }: any = useContext(AppContext);

  const [redirectionURL, setRedirectionURL] = useState('');
  const [journeyStarted, setJourneyStarted] = useState(false); // Track whether the journey has started
  const isPartial = useRef<boolean>(partialLoading);
  const navigate = useNavigate();

  // SIB only: the initiate-consent call (and the loader/OTP screens that
  // follow it) is deferred until the user acknowledges the AA notes shown
  // on ConsentNotesGate. Until then, any SIB FIP-selection dispatch is
  // stashed here instead of firing immediately.
  const [sibNotesAcknowledged, setSibNotesAcknowledged] = useState(false);
  const pendingSibFipSelection = useRef<{
    fipIds: string[];
    template: string[];
    withPartialLoading: boolean;
  } | null>(null);

  const gotoRoot = useCallback(() => {
    navigateToDashboard();
  }, [navigateToDashboard]);

  const goBackOnJourneyFailure = () => {
    if (currentFlowType === FlowType.BANKS) {
      if (!isAdditionalAccountJourney) {
        setTimeout(() => {
          setAAInternalStep(AASTEPS.AA_JOURNEY);
        }, 0);
        setIsInfoAvailable(true);
        handleFIPSelection(
          [],
          [ConsentType.PFM_BANKS, ConsentType.PFM_BANKS, ConsentType.PFM_BANKS]
        );
        setPartialLoading(true);
      } else {
        gotoRoot();
      }
    } else if (currentFlowType === FlowType.STOCKS) {
      gotoRoot();
    }
  };

  const createConsentForUser = async (
    fipIds: string[],
    templateType: string[]
  ) => {
    try {
      const meta = getFiTypeMeta(currentFlowType);
      pushEventToParent({
        eventCode: 'CONSENT_CREATION_INITIATED',
        isFromAA: true,
        fiType: currentFlowType ? FlowType[currentFlowType] : undefined,
        meta: meta,
        ...(isAdditionalAccountJourney
          ? {
              postHogEventName: `${FlowType[currentFlowType]}_CONSENT_CREATION_INITIATED`,
            }
          : {}),
      });

      if (getTenant() === TenantType.BOB || getTenant() === TenantType.BOB_V2) {
        setPartialLoading(true);
      }
      const initiateConsentResponse: any = await initiateConsent({
        fipIds: fipIds,
        templateType: templateType,
        fiIntegrationType: FIIntegrationType.NON_ETB,
      });

      if (initiateConsentResponse?.data === 'ERROR') {
        if (getTenant() === TenantType.JFS) {
          setShowToastAA(true);

          if (statusData.current.approvedConsentsAvailable) {
            setCurrentRoute(ROUTES.DASHBOARD);
          } else {
            setCurrentRoute(ROUTES.JFS_WELCOME);
            navigate('/welcome');
          }
        } else if (getTenant() === TenantType.AU) {
          setShowAUErrorSheet(true);
          setCurrentRoute(ROUTES.DASHBOARD);
        } else if (getTenant() === TenantType.SIB) {
          setSibErrorSheetMessage(SIB_CONSENT_INIT_FAILURE_MESSAGE);
          setShowSIBErrorSheet(true);
        } else {
          isError();
        }

        try {
          let meta: any = { data: JSON.stringify(initiateConsentResponse) };
          meta = { ...meta, ...getFiTypeMeta(currentFlowType) };
          pushEventToParent({
            eventCode: 'CONSENT_CREATION_FAILED',
            meta: meta,
            ...(isAdditionalAccountJourney
              ? {
                  postHogEventName: `${FlowType[currentFlowType]}_CONSENT_CREATION_FAILED`,
                }
              : {}),
          });
        } catch (e) {
          captureException(e);
        }

        return;
      } else {
        const meta = getFiTypeMeta(currentFlowType);
        pushEventToParent({
          eventCode: 'CONSENT_CREATION_SUCCESS',
          meta: meta,
          ...(isAdditionalAccountJourney
            ? {
                postHogEventName: `${FlowType[currentFlowType]}_CONSENT_CREATION_SUCCESS`,
              }
            : {}),
        });
      }
      setReferenceId(initiateConsentResponse.data.referenceId);

      if (!initiateConsentResponse.data.redirectionUrl) {
        // message.error(JSON.stringify(initiateConsentResponse));
        return;
      }

      if (getTenant() === TenantType.YESBANK) {
        if (getAltJourney() === altJourneyType.YESONLINE) {
          setRedirectionURL(
            `${initiateConsentResponse.data.redirectionUrl}?altFlow=YESONLINE`
          );
        } else {
          setRedirectionURL(`${initiateConsentResponse.data.redirectionUrl}`);
        }
      } else if ( getTenant() === TenantType.SIB ) {
        const baseUrl = initiateConsentResponse.data.redirectionUrl;
        setRedirectionURL(
          `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}forceWebEvents=true`,
        );
      } else if (getTenant() === TenantType.AU) {
        const baseUrl = initiateConsentResponse.data.redirectionUrl;
        let url = baseUrl.includes('?')
          ? `${baseUrl}&isAubank=true`
          : `${baseUrl}?isAubank=true`;
        if (getIsNtb()) {
          url += '&version=NTB';
        }
        setRedirectionURL(url);
      } else if (getTenant() === TenantType.MAHINDRA) {
        const url = initiateConsentResponse.data.redirectionUrl;
        const isDarkMode = getMahindraThemeMode?.() === 'dark';
        const finalUrl = url.includes('?')
          ? `${url}&darkMode=${isDarkMode}`
          : `${url}?darkMode=${isDarkMode}`;
        setRedirectionURL(finalUrl);
      } else if (getIsLoanJourney()) {
        setRedirectionURL(`${initiateConsentResponse.data.redirectionUrl}`);
      } else if (getTenant() === TenantType.PB && !getIsLoanJourney()) {
        setRedirectionURL(
          `${initiateConsentResponse.data.redirectionUrl}?customLogo=PBLOGO`
        );
      } else if (getTenant() === TenantType.JFS) {
        const url = initiateConsentResponse.data.redirectionUrl;
        if (getJfsTheme() === JfsTheme.Purple) {
          const finalUrl = url.includes('?')
            ? `${url}&version=v3`
            : `${url}?version=v3`;
          setRedirectionURL(finalUrl);
        } else {
          setRedirectionURL(url);
        }
      } else if (
        getTenant() === TenantType.BOB ||
        getTenant() === TenantType.BOB_V2
      ) {
        const url = initiateConsentResponse.data.redirectionUrl;
        
        const finalUrl = url.includes('?')
          ? `${url}&forceWebEvents=true`
          : `${url}?forceWebEvents=true`;
        setRedirectionURL(finalUrl);
      } else {
        setRedirectionURL(`${initiateConsentResponse.data.redirectionUrl}`);
      }
      setAAInternalStep(AASTEPS.AA_JOURNEY);
    } catch (e: any) {
      captureException(e);
      const meta = getFiTypeMeta(currentFlowType);
      pushEventToParent({
        eventCode: 'CONSENT_CREATION_FAILED',
        isFromAA: true,
        fiType: currentFlowType ? FlowType[currentFlowType] : undefined,
        meta: meta,
        ...(isAdditionalAccountJourney
          ? {
              postHogEventName: `${FlowType[currentFlowType]}_CONSENT_CREATION_FAILED`,
            }
          : {}),
      });

      if (getTenant() === TenantType.SIB) {
        setSibErrorSheetMessage(SIB_CONSENT_INIT_FAILURE_MESSAGE);
        setShowSIBErrorSheet(true);
      }
    }
  };

  const handleFIPSelection = (fipIds: string[], template: string[]) => {
    if (fipIds.length > 0) {
      pushEventToParent({
        eventCode: 'FIP_SELECTED',
        meta: {
          fipId: fipIds,
          fipName: fipIds,
        },
      });
    }
    setSelectedFIP(fipIds);
    createConsentForUser(fipIds, template);
    setJourneyStarted(true);
  };

  // SIB only: defers the actual FIP selection / initiate-consent dispatch
  // until the user has acknowledged ConsentNotesGate.
  const triggerSibFipSelection = (
    fipIds: string[],
    template: string[],
    withPartialLoading: boolean,
  ) => {
    if (sibNotesAcknowledged) {
      if (withPartialLoading) {
        setPartialLoading(true);
      }
      handleFIPSelection(fipIds, template);
    } else {
      pendingSibFipSelection.current = { fipIds, template, withPartialLoading };
    }
  };

  useEffect(() => {
    if (sibNotesAcknowledged && pendingSibFipSelection.current) {
      const { fipIds, template, withPartialLoading } =
        pendingSibFipSelection.current;
      pendingSibFipSelection.current = null;
      if (withPartialLoading) {
        setPartialLoading(true);
      }
      handleFIPSelection(fipIds, template);
    }
  }, [sibNotesAcknowledged]);

  useEffect(() => {
    if (
      (getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.MAHINDRA) &&
      !journeyStarted
    ) {
      if (selectedFIP.length > 0 && currentFlowType === FlowType.BANKS) {
        handleFIPSelection(selectedFIP, [ConsentType.PFM_RD_FD]);
      } else if (
        selectedFIP.length > 0 &&
        currentFlowType === FlowType.STOCKS
      ) {
        setPartialLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          createConsentForUser(
            [FipId.CDSL, FipId.NSDL],
            [ConsentType.PFM_STOCKS, ConsentType.PFM_STOCKS]
          );
        } else {
          createConsentForUser(
            [FipId.MOCK_FIP],
            [ConsentType.PFM_STOCKS, ConsentType.PFM_STOCKS]
          );
        }
      } else if (
        selectedFIP.length > 0 &&
        currentFlowType === FlowType.MUTUAL_FUNDS
      ) {
        setPartialLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          handleFIPSelection(
            [FipId.CAMS, FipId.CDSL, FipId.KFINTECH_MF, FipId.NSDL],
            [ConsentType.PFM_MF, ConsentType.PFM_MF, ConsentType.PFM_MF]
          );
        } else {
          handleFIPSelection(
            [],
            [ConsentType.PFM_MF, ConsentType.PFM_MF, ConsentType.PFM_MF]
          );
        }
      }
    } else if (getTenant() === TenantType.SIB && !journeyStarted) {
      if (selectedFIP.length > 0) {
        triggerSibFipSelection(
          selectedFIP,
          [ConsentType.PFM_BANKS, ConsentType.PFM_FD],
          false,
        );
      }
    }
  }, [journeyStarted, selectedFIP]);

  useEffect(() => {
    window.onpopstate = () => {
      if (isAdditionalAccountJourney || (isCommonTenant() && previousRoute)) {
        setIsLoading(false);
        gotoRoot();
      } else {
        setIsLoading(false);
        pushEventToParent({
          eventCode: EVENTCODES.BACK_BUTTON_PFMDB,
        });
        pushEventToParent({
          eventCode: 'BACK_BUTTON_PRESSED',
        });
      }
    };
    return () => {
      window.onpopstate = () => {};
    };
  }, [gotoRoot, isAdditionalAccountJourney, previousRoute, setIsLoading]);

  useEffect(() => {
    isPartial.current = partialLoading;
  }, [partialLoading]);

  useEffect(() => {
    if (getTenant() === TenantType.PB) {
      window.onpopstate = () => {
        // Push the user forward again to prevent back
        window.history.pushState(null, '', window.location.href);
      };
    }
  }, []);

  useEffect(() => {
    if (getTenant() === TenantType.MAHINDRA && selectedFIP.length <= 0) {
      setPartialLoading(true);
      handleFIPSelection([], [ConsentType.PFM_BANKS]);
    } else if (getTenant() === TenantType.YESBANK && selectedFIP.length <= 0) {
      if (currentFlowType === FlowType.MUTUAL_FUNDS) {
        setPartialLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          handleFIPSelection(
            [FipId.CAMS, FipId.CDSL, FipId.KFINTECH_MF, FipId.NSDL],
            [ConsentType.PFM_MF, ConsentType.PFM_MF, ConsentType.PFM_MF]
          );
        } else {
          handleFIPSelection(
            [FipId.MOCK_FIP, FipId.MOCK_MF_CAMS],
            [ConsentType.PFM_MF, ConsentType.PFM_MF, ConsentType.PFM_MF]
          );
        }
      } else if (currentFlowType === FlowType.STOCKS) {
        setPartialLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          createConsentForUser(
            [FipId.CDSL, FipId.NSDL],
            [ConsentType.PFM_STOCKS, ConsentType.PFM_STOCKS]
          );
        } else {
          createConsentForUser(
            [FipId.MOCK_FIP],
            [ConsentType.PFM_STOCKS, ConsentType.PFM_STOCKS]
          );
        }
      } else {
        setPartialLoading(true);
        handleFIPSelection(
          [],
          [
            ConsentType.PFM_RD_FD,
            ConsentType.PFM_RD_FD,
            ConsentType.PFM_RD_FD,
            ConsentType.PFM_RD_FD,
          ]
        );
      }
    } else if (getTenant() === TenantType.SIB && selectedFIP.length <= 0) {
      if (currentFlowType === FlowType.BANKS) {
        triggerSibFipSelection(
          [],
          [
            ConsentType.PFM_BANKS,
            ConsentType.PFM_BANKS,
            ConsentType.PFM_BANKS,
            ConsentType.PFM_BANKS,
            ConsentType.PFM_FD,
            ConsentType.PFM_FD,
          ],
          true,
        );
      }
    } else if (getTenant() === TenantType.PB) {
      if (currentFlowType === FlowType.BANKS) {
        if (
          isAdditionalAccountJourney ||
          (!isAdditionalAccountJourney && !isLoanJourney.current)
        ) {
          setPartialLoading(true);
        }

        if (!isAdditionalAccountJourney && !isLoanJourney.current) {
          if (getSdkFlowSelection()) {
            setAAInternalStep(AASTEPS.FIP_SELECTION);
          } else {
            handleFIPSelection(
              [],
              [
                ConsentType.PFM_BANKS,
                ConsentType.PFM_BANKS,
                ConsentType.PFM_BANKS,
              ]
            );
          }
        } else {
          setAAInternalStep(AASTEPS.FIP_SELECTION);
        }
      } else if (currentFlowType === FlowType.STOCKS) {
        setIsLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          createConsentForUser(
            [FipId.CDSL, FipId.NSDL],
            [ConsentType.PFM_STOCKS, ConsentType.PFM_STOCKS]
          );
        } else {
          createConsentForUser(
            [FipId.MOCK_FIP],
            [ConsentType.PFM_STOCKS, ConsentType.PFM_STOCKS]
          );
        }
      }
    } else if (isCommonTenant()) {
      if (currentFlowType === FlowType.BANKS) {
        if (
          isAdditionalAccountJourney ||
          (!isAdditionalAccountJourney && !isLoanJourney.current)
        ) {
          setPartialLoading(true);
        }

        if (!isAdditionalAccountJourney && !isLoanJourney.current) {
          if (getSdkFlowSelection()) {
            setAAInternalStep(AASTEPS.FIP_SELECTION);
          } else {
            handleFIPSelection([], ['PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS']);
          }
        } else {
          setAAInternalStep(AASTEPS.FIP_SELECTION);
        }
      } else if (currentFlowType === FlowType.STOCKS) {
        setIsLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          createConsentForUser(
            ['CDSLFIP', 'fip@nsdl'],
            ['PFM_STOCKS', 'PFM_STOCKS']
          );
        } else {
          createConsentForUser([], ['PFM_STOCKS', 'PFM_STOCKS']);
        }
      } else if (currentFlowType === FlowType.MUTUAL_FUNDS) {
        setIsLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          createConsentForUser(
            ['CAMSRTAFIP', 'CDSLFIP', 'kfinmf-fip', 'fip@nsdl'],
            ['PFM_MF', 'PFM_MF', 'PFM_MF', 'PFM_MF']
          );
        } else {
          createConsentForUser([], ['PFM_MF', 'PFM_MF']);
        }
      } else if (currentFlowType === FlowType.TERM_DEPOSITS) {
        setIsLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          createConsentForUser([], ['PFM_FD', 'PFM_FD', 'PFM_FD', 'PFM_FD']);
        } else {
          createConsentForUser(
            ['FIP-ID'],
            ['PFM_FD', 'PFM_FD', 'PFM_FD', 'PFM_FD']
          );
        }
      } else if (currentFlowType === FlowType.NPS) {
        setIsLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          createConsentForUser([], ['PFM_NPS', 'PFM_NPS']);
        } else {
          createConsentForUser(['FIP-ID'], ['PFM_NPS', 'PFM_NPS']);
        }
      }
    } else if (getTenant() === TenantType.JFS) {
      if (AAInternalStep === AASTEPS.AA_JOURNEY) {
        setPartialLoading(true);
        if (currentFlowType === FlowType.STOCKS) {
          if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              [FipId.NSDL, FipId.CDSL],
              [
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
              ]
            );
          } else {
            handleFIPSelection(
              [],
              [
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
              ]
            );
          }
        } else if (currentFlowType === FlowType.MUTUAL_FUNDS) {
          if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              [FipId.CAMS, FipId.CDSL, FipId.KFINTECH_MF, FipId.NSDL],
              [
                ConsentType.PFM_MF,
                ConsentType.PFM_MF,
                ConsentType.PFM_MF,
                ConsentType.PFM_MF,
              ]
            );
          } else {
            handleFIPSelection(
              [],
              [
                ConsentType.PFM_MF,
                ConsentType.PFM_MF,
                ConsentType.PFM_MF,
                ConsentType.PFM_MF,
              ]
            );
          }
        } else if (currentFlowType === FlowType.BANKS) {
          handleFIPSelection(
            [],
            [
              ConsentType.PFM_BANKS,
              ConsentType.PFM_BANKS,
              ConsentType.PFM_BANKS,
              ConsentType.PFM_BANKS,
              ConsentType.PFM_BANKS,
              ConsentType.PFM_FD,
              ConsentType.PFM_FD,
            ]
          );
        } else if (currentFlowType === FlowType.TERM_DEPOSITS) {
          handleFIPSelection(fdSelectedFips, [
            ConsentType.PFM_FD,
            ConsentType.PFM_FD,
            ConsentType.PFM_FD,
            ConsentType.PFM_FD,
            ConsentType.PFM_FD,
            ConsentType.PFM_BANKS,
            ConsentType.PFM_BANKS,
          ]);
        } else if (currentFlowType === FlowType.NPS) {
          if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              [FipId.NPS_PROD, FipId.KFINTECH_NPS, FipId.CAMS_NPS],
              [
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
              ]
            );
          } else {
            handleFIPSelection(
              [FipId.MOCK_FIP, FipId.MOCK_NPS],
              [
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
              ]
            );
          }
        }
      } else {
        setPartialLoading(true);
        setAAInternalStep(AASTEPS.FIP_SELECTION);
      }
    } else if (getTenant() === TenantType.AU) {
      if (AAInternalStep === AASTEPS.AA_JOURNEY) {
        setPartialLoading(true);
        const hasTargetedFIPs = selectedFIP?.length > 0;
        if (currentFlowType === FlowType.STOCKS) {
          if (hasTargetedFIPs) {
            handleFIPSelection(selectedFIP, [
              ConsentType.PFM_STOCKS,
              ConsentType.PFM_STOCKS,
              ConsentType.PFM_STOCKS,
              ConsentType.PFM_STOCKS,
            ]);
          } else if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              [FipId.NSDL, FipId.CDSL],
              [
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
              ]
            );
          } else {
            handleFIPSelection(
              [],
              [
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
                ConsentType.PFM_STOCKS,
              ]
            );
          }
        } else if (currentFlowType === FlowType.MUTUAL_FUNDS) {
          if (hasTargetedFIPs) {
            handleFIPSelection(selectedFIP, [
              ConsentType.PFM_MF,
              ConsentType.PFM_MF,
              ConsentType.PFM_MF,
            ]);
          } else if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              [FipId.CAMS, FipId.CDSL, FipId.KFINTECH_MF, FipId.NSDL],
              [ConsentType.PFM_MF, ConsentType.PFM_MF, ConsentType.PFM_MF]
            );
          } else {
            handleFIPSelection(
              [],
              [ConsentType.PFM_MF, ConsentType.PFM_MF, ConsentType.PFM_MF]
            );
          }
        } else if (currentFlowType === FlowType.BANKS) {
          handleFIPSelection(
            hasTargetedFIPs ? selectedFIP : [],
            [
              ConsentType.PFM_BANKS,
              ConsentType.PFM_BANKS,
              ConsentType.PFM_BANKS,
              ConsentType.PFM_BANKS,
              ConsentType.PFM_BANKS,
            ]
          );
        } else if (currentFlowType === FlowType.TERM_DEPOSITS) {
          handleFIPSelection(
            hasTargetedFIPs ? selectedFIP : [],
            [
              ConsentType.PFM_FD,
              ConsentType.PFM_FD,
              ConsentType.PFM_FD,
              ConsentType.PFM_FD,
            ]
          );
        } else if (currentFlowType === FlowType.NPS) {
          if (hasTargetedFIPs) {
            handleFIPSelection(selectedFIP, [
              ConsentType.PFM_NPS,
              ConsentType.PFM_NPS,
              ConsentType.PFM_NPS,
              ConsentType.PFM_NPS,
            ]);
          } else if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              [FipId.NPS_PROD, FipId.KFINTECH_NPS, FipId.CAMS_NPS],
              [
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
              ]
            );
          } else {
            handleFIPSelection(
              [FipId.MOCK_FIP, FipId.MOCK_NPS],
              [
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
                ConsentType.PFM_NPS,
              ]
            );
          }
        } else if (currentFlowType === FlowType.INSURANCE) {
          setAAInternalStep(AASTEPS.FIP_SELECTION);
        }
      }
    }
  }, []);

  const renderComponents = () => {
    switch (AAInternalStep) {
      case AASTEPS.FIP_SELECTION:
        return (
          <FIPSelection
            onSelect={handleFIPSelection}
            isAdditionalAccountJourney={isAdditionalAccountJourney}
            gotoRoot={gotoRoot}
          />
        );
      case AASTEPS.CONSENT_CREATION:
        return <></>;
      case AASTEPS.AA_JOURNEY:
        return (
          <AAWrapper
            redirectionURL={redirectionURL}
            isInfoAvailable={isInfoAvailable}
            setIsInfoAvailable={setIsInfoAvailable}
            gotoRoot={gotoRoot}
            goBackOnJourneyFailure={goBackOnJourneyFailure}
            setAAInternalStep={setAAInternalStep}
            isPartial={isPartial}
            partialLoading={partialLoading}
            setPartialLoading={setPartialLoading}
            sibNotesAcknowledged={sibNotesAcknowledged}
            onAcknowledgeSibNotes={() => setSibNotesAcknowledged(true)}
          />
        );
      case AASTEPS.AA_JOURNEY_COMPLETED:
        return <></>;
      case AASTEPS.CONSENT_FAILURE:
        return (
          <ConsentFailure goBackOnJourneyFailure={goBackOnJourneyFailure} />
        );
      default:
        return <></>;
    }
  };

  return <>{renderComponents()}</>;
};

export default AA;
